import { client } from '../apiClient';
import { BotsActivityOverviewResponse } from '../../types/bots-activity/BotsActivityOverviewResponse';
import { BotsActivityTimeseriesResponse } from '../../types/bots-activity/BotsActivityTimeseriesResponse';
import { BotsActivityContentResponse } from '../../types/bots-activity/BotsActivityContentResponse';
import { BotsActivityStatusResponse } from '../../types/bots-activity/BotsActivityStatusResponse';

const ENDPOINT = '/bots-activity';

type RawBotsActivityContentRow = {
  path?: string;
  count?: number | string;
  totalCount?: number | string;
  previousCount?: number | string;
  lastSeenAt?: string | null;
  bots?: string[];
};

type RawBotsActivityContentResponse = {
  content: RawBotsActivityContentRow[];
  excludedFileVisits?: number;
};

const computeChangePct = (current: number, previous: number): number | null => {
  if (previous > 0) return Math.round(((current - previous) / previous) * 100);
  return null;
};

const normalizeContentResponse = (
  response: RawBotsActivityContentResponse
): BotsActivityContentResponse => ({
  content: response.content.map((row) => {
    const count = Number(row.count ?? row.totalCount ?? 0);
    const previous = Number(row.previousCount ?? 0);
    return {
      path: row.path ?? '/',
      count,
      bots: row.bots ?? [],
      changePct: row.previousCount != null ? computeChangePct(count, previous) : null,
      lastSeenAt: row.lastSeenAt ?? null,
    };
  }),
  excludedFileVisits:
    typeof response.excludedFileVisits === 'number'
      ? response.excludedFileVisits
      : undefined,
});

export const getOverview = async (
  projectId: number,
  range: string
): Promise<BotsActivityOverviewResponse> => {
  const { data } = await client(true, true).get<BotsActivityOverviewResponse>(
    `${ENDPOINT}/${projectId}/overview`,
    { params: { range } }
  );
  return data;
};

export const getTimeseries = async (
  projectId: number,
  range: string,
  granularity: 'hour' | 'day' | 'week'
): Promise<BotsActivityTimeseriesResponse> => {
  const { data } = await client(true, true).get<BotsActivityTimeseriesResponse>(
    `${ENDPOINT}/${projectId}/timeseries`,
    { params: { range, granularity } }
  );
  return data;
};

export const getContent = async (
  projectId: number,
  range: string,
  groupBy: 'folder' | 'url',
  limit: number = 10,
  bots?: string[] | null
): Promise<BotsActivityContentResponse> => {
  const { data } = await client(true, true).get<RawBotsActivityContentResponse>(
    `${ENDPOINT}/${projectId}/content`,
    {
      params: {
        range,
        groupBy,
        limit,
        // Backend filters all content aggregates to these bots (CSV param).
        ...(bots && bots.length > 0 ? { bots: bots.join(',') } : {}),
      },
    }
  );
  return normalizeContentResponse(data);
};

export const getStatus = async (
  projectId: number,
  range: string = '7d'
): Promise<BotsActivityStatusResponse> => {
  const { data } = await client(true, true).get<BotsActivityStatusResponse>(
    `${ENDPOINT}/${projectId}/status`,
    { params: { range } }
  );
  return data;
};

export type RunAnalysisStatus =
  | 'ok'
  | 'no_integration'
  | 'inactive'
  | 'rate_limited'
  | 'cloudflare_rate_limited'
  | 'fastly_rate_limited'
  | 'auth_failed'
  | 'failed';

export type RunAnalysisSource = 'cloudflare' | 'fastly' | 'log_upload';

export interface RunAnalysisResponse {
  status: RunAnalysisStatus;
  /**
   * Which provider serviced the manual poll. Returned by the backend on
   * `status='ok'` (and `'log_upload'` for log aggregation). Used by the FE
   * to pick a source-specific success/error message instead of hardcoding
   * "Cloudflare" everywhere.
   */
  source?: RunAnalysisSource;
  rowsIngested?: number;
  lastPollAt?: string | null;
  retryAfterSeconds?: number;
  error?: string;
}

export const runAnalysis = async (projectId: number): Promise<RunAnalysisResponse> => {
  const { data } = await client(true, true).post<RunAnalysisResponse>(
    `${ENDPOINT}/${projectId}/run-analysis`
  );
  return data;
};
